import { useBitcoinWallet } from '@/hooks/useBitcoinWallet';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatSats, satsToUSD } from '@/lib/bitcoin';
import type { Transaction } from '@/lib/bitcoin';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Copy,
  ExternalLink,
  RefreshCw,
  Wallet,
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

interface WalletPanelProps {
  onSend?: () => void;
}

export function WalletPanel({ onSend }: WalletPanelProps) {
  const { user } = useCurrentUser();
  const { bitcoinAddress, addressData, btcPrice, transactions, isLoading, isLoadingTxs, refetch } =
    useBitcoinWallet();
  const { toast } = useToast();

  function copyAddress() {
    if (!bitcoinAddress) return;
    navigator.clipboard.writeText(bitcoinAddress).then(() => {
      toast({ title: 'Address copied!' });
    });
  }

  if (!user) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 text-center">
          <Wallet className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">Log in to see your wallet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Balance Card */}
      <Card className="bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg">
        <CardContent className="pt-6 pb-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium mb-1">Your Balance</p>
              {isLoading ? (
                <Skeleton className="h-9 w-32 bg-orange-400/50" />
              ) : (
                <p className="text-3xl font-bold tracking-tight">
                  {formatSats(addressData?.totalBalance ?? 0)}{' '}
                  <span className="text-xl font-semibold opacity-80">sats</span>
                </p>
              )}
              {btcPrice && addressData && (
                <p className="text-orange-100 text-sm mt-0.5">
                  ≈ {satsToUSD(addressData.totalBalance, btcPrice)}
                </p>
              )}
              {addressData?.pendingBalance !== 0 && addressData?.pendingBalance !== undefined && (
                <Badge className="mt-2 bg-orange-600/50 text-orange-100 border-0 text-xs">
                  +{formatSats(addressData.pendingBalance)} pending
                </Badge>
              )}
            </div>
            <button
              onClick={refetch}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              title="Refresh balance"
            >
              <RefreshCw className="w-4 h-4 opacity-80" />
            </button>
          </div>

          <Separator className="my-4 bg-orange-400/40" />

          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-orange-100 truncate flex-1">
              {bitcoinAddress || '—'}
            </span>
            <button
              onClick={copyAddress}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors shrink-0"
              title="Copy address"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            {bitcoinAddress && (
              <a
                href={`https://mempool.space/address/${bitcoinAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors shrink-0"
                title="View on mempool.space"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Send button */}
      <Button
        className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 text-base font-semibold"
        onClick={onSend}
        disabled={!addressData || addressData.totalBalance === 0}
      >
        <ArrowUpRight className="w-5 h-5 mr-2" />
        Burn Sats
      </Button>

      {/* Transaction History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Transaction History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoadingTxs ? (
            <div className="space-y-3 px-4 pb-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          ) : transactions && transactions.length > 0 ? (
            <div className="divide-y">
              {transactions.slice(0, 20).map((tx) => (
                <TxRow key={tx.txid} tx={tx} btcPrice={btcPrice} />
              ))}
            </div>
          ) : (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground">No transactions yet.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Send some sats to your address above to get started.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TxRow({ tx, btcPrice }: { tx: Transaction; btcPrice: number | undefined }) {
  const isReceive = tx.type === 'receive';
  const date = tx.timestamp ? new Date(tx.timestamp * 1000) : null;

  return (
    <a
      href={`https://mempool.space/tx/${tx.txid}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors group"
    >
      <div
        className={cn(
          'rounded-full p-1.5 shrink-0',
          isReceive ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
        )}
      >
        {isReceive ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {isReceive ? 'Received' : 'Sent'}
          {!tx.confirmed && (
            <Badge variant="outline" className="ml-2 text-[10px] py-0 h-4">
              Pending
            </Badge>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {date
            ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
            : 'Unconfirmed'}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className={cn('text-sm font-mono font-medium', isReceive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
          {isReceive ? '+' : '-'}{formatSats(tx.amount)} sats
        </p>
        {btcPrice && (
          <p className="text-xs text-muted-foreground">
            {satsToUSD(tx.amount, btcPrice)}
          </p>
        )}
      </div>
    </a>
  );
}
